import type { ReactNode } from "react";
import { useFormContext, useController } from "@leagueplatform/web-common";
import { useIntl } from "@leagueplatform/locales";
import {
  Box,
  Checkbox,
  InputStatusMessage,
  type GDSCheckboxProps,
} from "@leagueplatform/genesis-core";
import type { RHFInputBaseProps } from "./rhf-input.types";

export type RHFCheckboxProps = Omit<RHFInputBaseProps, "label"> & {
  label: ReactNode;
} & Omit<GDSCheckboxProps, "id" | "name" | "checked" | "label" | "disabled">;

export const RHFCheckbox = ({
  id,
  label,
  required,
  requiredMessage,
  registerOptions,
  disabled,
  ...restComponentProps
}: RHFCheckboxProps) => {
  const { control } = useFormContext();
  const { formatMessage } = useIntl();

  const defaultRequiredMessage = formatMessage(
    { id: "STR_FIELD_REQUIRED" },
    { fieldName: typeof label === "string" ? label : id }
  );

  const { field, fieldState } = useController({
    name: id,
    control,
    rules: {
      ...registerOptions,
      required: required && (requiredMessage || defaultRequiredMessage),
    },
  });

  const hasError = !!fieldState.error?.message;

  return (
    <Box>
      <Checkbox
        id={id}
        name={id}
        label={label}
        checked={!!field.value}
        onChange={(e) => field.onChange(e.target.checked)}
        onBlur={field.onBlur}
        inputStatus={hasError ? "error" : undefined}
        disabled={disabled}
        {...restComponentProps}
      />
      {hasError && (
        <InputStatusMessage
          inputStatus="error"
          id={`${id}-error`}
          css={{
            marginBlockStart: "$none",
            marginInlineStart: "$half",
          }}
        >
          {fieldState.error?.message}
        </InputStatusMessage>
      )}
    </Box>
  );
};
